/**
 * Helper para obtener URLs de imágenes locales o CDN según la configuración
 * Si NEXT_PUBLIC_USE_LOCAL_IMAGES=true y la imagen está soportada, usa local
 * Si no, usa el CDN como antes
 */

import { isTeamSupported, isPlayerSupported, isLogoSupported, isLottieSupported, SUPPORTED_LOGOS, SUPPORTED_LOTTIES } from './supported-images';

// Leer la variable de entorno - se evalúa en tiempo de compilación
const USE_LOCAL_IMAGES = process.env.NEXT_PUBLIC_USE_LOCAL_IMAGES === 'true';

// Debug: mostrar el valor en desarrollo
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('🔍 USE_LOCAL_IMAGES:', USE_LOCAL_IMAGES, 'from env:', process.env.NEXT_PUBLIC_USE_LOCAL_IMAGES);
}

/**
 * Obtiene la URL de una imagen de equipo (local o CDN)
 * @param {number|string} teamId - ID del equipo
 * @param {number} width - Ancho de la imagen
 * @param {number} height - Alto de la imagen
 * @param {number} version - Versión para cache busting
 * @returns {string} URL de la imagen
 */
export const getTeamImageUrl = (teamId, width, height, version = 10) => {
    if (!teamId) return null;

    const id = typeof teamId === 'number' ? teamId : Number(teamId);
    if (isNaN(id)) return null;

    const isSupported = isTeamSupported(id);

    // Si está habilitado usar locales y el equipo está soportado
    if (USE_LOCAL_IMAGES && isSupported) {
        const localUrl = `/assets/equipos/${id}.png`;
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
            console.log(`✅ Usando imagen local para equipo ${id}:`, localUrl);
        }
        return localUrl;
    }

    // Usar CDN como antes
    const addWidth = `?width=${width}&height=${height}&version=${version}`;
    const cdnUrl = `${process.env.NEXT_PUBLIC_IMAGES}/equipos/${id}.png${addWidth}`;
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && USE_LOCAL_IMAGES) {
        console.log(`⚠️ Equipo ${id} no soportado localmente, usando CDN:`, cdnUrl, 'isSupported:', isSupported);
    }
    return cdnUrl;
};

/**
 * Obtiene la URL de una imagen de jugador (local o CDN)
 * @param {number|string} playerId - ID del jugador
 * @param {number} width - Ancho de la imagen
 * @param {number} height - Alto de la imagen
 * @param {number} version - Versión para cache busting
 * @returns {string} URL de la imagen
 */
export const getPlayerImageUrl = (playerId, width, height, version = 26) => {
    if (!playerId) return null;

    const id = typeof playerId === 'number' ? playerId : Number(playerId);
    if (isNaN(id)) return null;

    // Si está habilitado usar locales y el jugador está soportado
    if (USE_LOCAL_IMAGES && isPlayerSupported(id)) {
        return `/assets/jugadores/${id}.png`;
    }

    // Usar CDN como antes
    const addWidth = `?width=${width}&height=${height}&version=${version}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/${id}.png${addWidth}`;
};

/**
 * Obtiene la URL de un logo (local o CDN)
 * @param {string} logoKey - Clave del logo (de SUPPORTED_LOGOS)
 * @returns {string} URL del logo
 */
export const getLogoUrl = (logoKey) => {
    if (!logoKey) return null;

    // Si está habilitado usar locales y el logo está soportado
    if (USE_LOCAL_IMAGES && isLogoSupported(logoKey)) {
        const logoFileName = SUPPORTED_LOGOS[logoKey] || logoKey;
        return `/assets/logos/${logoFileName}`;
    }

    // Usar CDN como antes
    // Asumiendo que los logos están en el CDN en una ruta similar
    return `${process.env.NEXT_PUBLIC_IMAGES}/logos/${logoKey}`;
};

/**
 * Obtiene la URL de una imagen por defecto de jugador
 * @returns {string} URL de la imagen por defecto
 */
export const getDefaultPlayerImageUrl = () => {
    if (USE_LOCAL_IMAGES) {
        return '/assets/jugadores/default-player.png';
    }
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/0.png`;
};

/**
 * Obtiene la URL de un logo (local o CDN)
 * @param {string} logoKey - Clave del logo (de SUPPORTED_LOGOS) o nombre del archivo
 * @returns {string} URL del logo
 */
export const getLogoImageUrl = (logoKey) => {
    if (!logoKey) return null;

    // Si está habilitado usar locales y el logo está soportado
    if (USE_LOCAL_IMAGES && isLogoSupported(logoKey)) {
        const logoFileName = SUPPORTED_LOGOS[logoKey] || logoKey;
        return `/assets/logos/${logoFileName}`;
    }

    // Usar CDN como antes
    return `${process.env.NEXT_PUBLIC_IMAGES}/logos/${logoKey}`;
};

/**
 * Obtiene la URL de un archivo Lottie (local o CDN)
 * @param {string} lottieKey - Clave del lottie (de SUPPORTED_LOTTIES) o ruta del archivo
 * @returns {string} URL del lottie
 */
export const getLottieUrl = (lottieKey) => {
    if (!lottieKey) return null;

    // Determinar la ruta del archivo
    // Si es una clave del enum, obtener el valor; si no, usar directamente
    let lottiePath = SUPPORTED_LOTTIES[lottieKey] || lottieKey;

    // Si está habilitado usar locales, verificar si el archivo está soportado
    // Verificamos tanto por clave como por valor (nombre del archivo)
    if (USE_LOCAL_IMAGES) {
        const isSupported = isLottieSupported(lottieKey) ||
            Object.values(SUPPORTED_LOTTIES).includes(lottieKey) ||
            Object.values(SUPPORTED_LOTTIES).includes(lottiePath);

        if (isSupported) {
            return `/assets/lotties/${lottiePath}`;
        }
    }

    // Usar CDN como antes
    return `${process.env.NEXT_PUBLIC_IMAGES}/lotties/${lottiePath}`;
};

